import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { ApiError, api, errorMessage } from './api';
import { identityEpoch, StaleIdentityError } from './identity';
import { SEED_UNAVAILABLE_FLAG } from '../../shared/siteRoutes';

// Minimal reactive query cache keyed by request path. Components subscribe to
// a key; mutations invalidate prefixes so every mounted view refetches.

type Status = 'idle' | 'loading' | 'success' | 'error';

interface Entry {
  status: Status;
  data: unknown;
  error: ApiError | null;
  fetching: boolean;
  updatedAt: number;
  inflight: Promise<void> | null;
  stale: boolean;
  requestId: number;
}

const cache = new Map<string, Entry>();
const subscribers = new Map<string, Set<() => void>>();
let requestSequence = 0;

function getEntry(key: string): Entry {
  let entry = cache.get(key);
  if (!entry) {
    entry = { status: 'idle', data: undefined, error: null, fetching: false, updatedAt: 0, inflight: null, stale: true, requestId: 0 };
    cache.set(key, entry);
  }
  return entry;
}

function notify(key: string): void {
  subscribers.get(key)?.forEach((fn) => fn());
}

function setEntry(key: string, patch: Partial<Entry>): void {
  const next = { ...getEntry(key), ...patch };
  cache.set(key, next);
  notify(key);
}

function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  return new ApiError(0, 'client_error', errorMessage(err));
}

/** Keys whose requests give up after a time (useQuery's timeoutMs): a hung request ends, so the next one can start. */
const timeouts = new Map<string, number>();

/** The request for `key`, aborted with an error (never left pending) past its timeout, if it has one. */
function request(key: string): Promise<unknown> {
  const ms = timeouts.get(key);
  if (!ms) return api(key);
  const controller = new AbortController();
  let timer: number | undefined;
  return Promise.race([
    api(key, { signal: controller.signal }),
    new Promise<never>((_resolve, reject) => {
      timer = window.setTimeout(() => {
        reject(new ApiError(0, 'timeout', 'The Perzo server took too long to answer.'));
        controller.abort();
      }, ms);
    }),
  ]).finally(() => window.clearTimeout(timer));
}

export function fetchKey(key: string, force = false): Promise<void> {
  const entry = getEntry(key);
  if (entry.inflight && !force) return entry.inflight;
  const requestId = ++requestSequence;
  const epoch = identityEpoch();
  const promise = request(key)
    .then((data) => {
      if (identityEpoch() !== epoch || getEntry(key).requestId !== requestId) return;
      setEntry(key, { status: 'success', data, error: null, fetching: false, updatedAt: Date.now(), inflight: null, stale: false });
    })
    .catch((err: unknown) => {
      if (identityEpoch() !== epoch || getEntry(key).requestId !== requestId || err instanceof StaleIdentityError || (err instanceof DOMException && err.name === 'AbortError')) return;
      setEntry(key, {
        status: 'error',
        error: toApiError(err),
        fetching: false,
        inflight: null,
        stale: false,
        // keep previous data visible during refetch errors
      });
    });
  setEntry(key, {
    requestId,
    inflight: promise,
    fetching: true,
    status: entry.status === 'success' ? 'success' : 'loading',
  });
  return promise;
}

/** Refetch mounted queries whose key starts with any prefix; drop the rest. */
export function invalidate(...prefixes: string[]): void {
  for (const key of Array.from(cache.keys())) {
    if (!prefixes.some((prefix) => key === prefix || key.startsWith(prefix.endsWith('/') ? prefix : prefix + '/') || key.startsWith(prefix + '?'))) continue;
    if (subscribers.get(key)?.size) void fetchKey(key, true);
    else cache.delete(key);
  }
}

export function clearQueryCache(): void {
  cache.clear();
  subscribers.forEach((set) => set.forEach((fn) => fn()));
}

/** Read already-loaded cache data without triggering a request. */
export function queryData<T>(key: string): T | undefined {
  return cache.get(key)?.data as T | undefined;
}

/**
 * Data a prerendered page was rendered with (public site pages: the plans DTO and the demo switch), by query key.
 * The server render and the browser's hydration both read it as useQuery's server snapshot, so the first client
 * render matches the HTML; primeQueryCache puts the same data in the cache, marked stale, so it is fetched again
 * once the page is live.
 */
export type QuerySeed = Readonly<Record<string, unknown>>;

const SeedContext = createContext<ReadonlyMap<string, Entry> | null>(null);

/**
 * A seed value meaning "the server could not get this data in time": the page renders (and hydrates) its error
 * state, and the browser asks again once live. Never stands for any data.
 */
export function isSeedUnavailable(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && (value as Record<string, unknown>)[SEED_UNAVAILABLE_FLAG] === true);
}

function seededEntry(value: unknown, updatedAt: number): Entry {
  if (isSeedUnavailable(value)) {
    const error = new ApiError(503, 'temporarily_unavailable', 'This information is temporarily unavailable.');
    return { status: 'error', data: undefined, error, fetching: false, updatedAt: 0, inflight: null, stale: true, requestId: 0 };
  }
  return { status: 'success', data: value, error: null, fetching: false, updatedAt, inflight: null, stale: true, requestId: 0 };
}

export function QuerySeedProvider({ seed, children }: { seed: QuerySeed | null; children: ReactNode }) {
  // updatedAt 0: the render's data has no browser-side age (only the primed cache entries do).
  const entries = useMemo(() => (seed ? new Map(Object.entries(seed).map(([key, data]) => [key, seededEntry(data, 0)])) : null), [seed]);
  return createElement(SeedContext.Provider, { value: entries }, children);
}

/**
 * Browser only, before hydrating a prerendered page: the seed becomes the cached (stale) data for its keys, aged
 * from now (the page was just served).
 */
export function primeQueryCache(seed: QuerySeed): void {
  const now = Date.now();
  for (const [key, data] of Object.entries(seed)) {
    if (!cache.get(key)?.data) cache.set(key, seededEntry(data, now));
  }
}

export interface QueryResult<T> {
  data: T | undefined;
  error: ApiError | null;
  /** First load with no data yet. */
  loading: boolean;
  /** Any request in flight, including background refetch. */
  fetching: boolean;
  /** When `data` was received (ms epoch); 0 when unknown (no data, or the server render's own data). */
  updatedAt: number;
  refetch: () => Promise<void>;
}

export interface QueryOptions {
  /** Poll interval in ms while mounted; undefined disables polling. */
  refetchInterval?: number | false;
  /** Skip the request (e.g. dependencies not ready). */
  enabled?: boolean;
  /** Give up on a request after this long (an error, so the next refetch can run). */
  timeoutMs?: number;
}

/** Marks `key` as mounted (what useQuery does): invalidation then refetches it instead of dropping it. */
export function subscribeKey(key: string, onChange: () => void): () => void {
  let set = subscribers.get(key);
  if (!set) {
    set = new Set();
    subscribers.set(key, set);
  }
  set.add(onChange);
  return () => {
    set?.delete(onChange);
  };
}

export function useQuery<T>(key: string | null, options: QueryOptions = {}): QueryResult<T> {
  const enabled = key !== null && options.enabled !== false;
  if (key && options.timeoutMs) timeouts.set(key, options.timeoutMs);
  const subscribe = useCallback((onChange: () => void) => (key ? subscribeKey(key, onChange) : () => undefined), [key]);
  const seeded = useContext(SeedContext);
  const snapshot = useSyncExternalStore(
    subscribe,
    () => (key ? getEntry(key) : null),
    () => (key ? (seeded?.get(key) ?? null) : null),
  );

  useEffect(() => {
    if (!enabled || !key) return;
    const entry = getEntry(key);
    if (entry.stale || entry.status === 'idle' || Date.now() - entry.updatedAt > 30_000) void fetchKey(key);
  }, [key, enabled]);

  useEffect(() => {
    if (!enabled || !key || !options.refetchInterval) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void fetchKey(key);
    }, options.refetchInterval);
    return () => window.clearInterval(timer);
  }, [key, enabled, options.refetchInterval]);

  const refetch = useCallback(() => (key ? fetchKey(key, true) : Promise.resolve()), [key]);

  return {
    data: snapshot?.data as T | undefined,
    error: snapshot?.error ?? null,
    loading: enabled && (!snapshot || snapshot.status === 'loading' || snapshot.status === 'idle') && snapshot?.data === undefined,
    fetching: Boolean(snapshot?.fetching),
    updatedAt: snapshot?.updatedAt ?? 0,
    refetch,
  };
}

export interface MutationState<TVars, TResult> {
  mutate: (vars: TVars) => Promise<TResult>;
  pending: boolean;
  error: ApiError | null;
  reset: () => void;
}

export function useMutation<TVars, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  opts: { invalidates?: string[]; onSuccess?: (result: TResult, vars: TVars) => void } = {},
): MutationState<TVars, TResult> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const mounted = useRef(true);
  const fnRef = useRef(fn);
  const optsRef = useRef(opts);
  fnRef.current = fn;
  optsRef.current = opts;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const mutate = useCallback(async (vars: TVars) => {
    const epoch = identityEpoch();
    setPending(true);
    setError(null);
    try {
      const result = await fnRef.current(vars);
      if (identityEpoch() !== epoch) throw new StaleIdentityError();
      if (optsRef.current.invalidates?.length) invalidate(...optsRef.current.invalidates);
      optsRef.current.onSuccess?.(result, vars);
      return result;
    } catch (err) {
      if (identityEpoch() !== epoch) throw new StaleIdentityError();
      const apiErr = toApiError(err);
      if (mounted.current) setError(apiErr);
      throw apiErr;
    } finally {
      if (mounted.current && identityEpoch() === epoch) setPending(false);
    }
  }, []);

  const reset = useCallback(() => setError(null), []);
  return { mutate, pending, error, reset };
}
